import PropTypes from 'prop-types';
import { WizardStep } from '@zillow/zrs-rental-applications';
import { Checkbox, FieldSet, Heading, Label, LabeledControl, Legend, Paragraph } from '@zillow/constellation';
import { useListingUser } from '../../../hooks/useListingUser';
import { emitDeclineContinueAfterReasonsEvent } from '../../../../client/analytics/clickstreamEvents';

const ReasonsStep = ({ id, label, selectedDeclineReasons, setSelectedDeclineReasons, allDeclineReasons }) => {
  const { user, listing } = useListingUser();

  const handleReasonSelect = ({ target: { value: reasonName } }) => {
    const currentReasons = new Set(selectedDeclineReasons);

    if (currentReasons.has(reasonName)) {
      currentReasons.delete(reasonName);
      return setSelectedDeclineReasons(currentReasons);
    }

    setSelectedDeclineReasons(currentReasons.add(reasonName));
  };

  return (
    <WizardStep
      id={id}
      label={label}
      meta={{
        isStepDisabled: !selectedDeclineReasons.size,
        hideBackButton: true,
      }}
      onRequestNext={() => emitDeclineContinueAfterReasonsEvent(listing, user)}
      data-test="reasons-step"
    >
      <Heading level={4} marginBottom="md" data-test="reasons-step__heading">
        Why are you declining this application?
      </Heading>
      <Paragraph marginBottom="md">
        {`Easily create a response to send to the applicant regarding their application status. You'll be able to see a preview of the email before you send it.`}
      </Paragraph>
      <Paragraph marginBottom="md">
        <strong>(Select all that apply)</strong>
      </Paragraph>
      <FieldSet
        appearance="none"
        marginBottom="xl"
        data-test="reasons-step__list"
        legend={<Legend>Decline reasons</Legend>}
      >
        {allDeclineReasons?.map(({ name, text }) => (
          <LabeledControl
            label={<Label>{text}</Label>}
            key={name}
            paddingY={0}
            control={
              <Checkbox
                value={name}
                checked={selectedDeclineReasons.has(name)}
                onChange={handleReasonSelect}
                data-test="reasons-step__checkbox"
              />
            }
          />
        ))}
      </FieldSet>
    </WizardStep>
  );
};

ReasonsStep.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  selectedDeclineReasons: PropTypes.instanceOf(Set),
  setSelectedDeclineReasons: PropTypes.func.isRequired,
  allDeclineReasons: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      additionalRequirement: PropTypes.string.isRequired,
    }),
  ),
};

export default ReasonsStep;
